import {Component, EventEmitter, inject, input, Output} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DataService} from '../../../services/store/data.service';
import {ManagerSettingsFormComponent} from './manager-settings-form/manager-settings-form.component';
import {ManagerSettingsDeleteComponent} from './manager-settings-delete/manager-settings-delete.component';
import {ManagerSettings} from '../../../classes/models/employee/ManagerSettings';
import {EmployeePositionId} from '../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';

/**
 * This component handles displaying and editing manager settings.
 */
@Component({
  selector: 'app-manager-settings',
  templateUrl: './manager-settings.component.html',
  styleUrls: ['./manager-settings.component.less'],
  imports: [SHARED_IMPORTS]
})
export class ManagerSettingsComponent {
  private modalService = inject(NgbModal);
  dataService = inject(DataService);

  /** Identifiers of an employee and position. */
  readonly employeePositionId = input<EmployeePositionId>(undefined);
  /** A list of manager settings. */
  readonly managerSettings = input<ManagerSettings[]>([]);
  /** An event to refresh employee page data. */
  @Output() getEmployeePageDataEvent = new EventEmitter();

  /**
   * Checks if editing is allowed on a specific date.
   * @param date a start date of manager settings.
   */
  catEditAtDate(date: string): boolean {
    return this.dataService.canEditAtDate(date);
  }

  /**
   * Opens a modal window to create a new manager setting.
   */
  openCreateSettings(): void {
    const modalRef = ManagerSettingsFormComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId()!,
      previousManagerSettings: undefined,
      weekExists: false
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal window to update a manager setting.
   */
  openUpdateSettings(managerSettings: ManagerSettings): void {
    const modalRef = ManagerSettingsFormComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId()!,
      previousManagerSettings: managerSettings,
      weekExists: true
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal window to delete an existing manager setting.
   * @param date a start date, from which a manager setting should be deleted.
   */
  openDeleteSettings(date: string): void {
    const modalRef = ManagerSettingsDeleteComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      date
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }
}